import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus, MapPin, Award, Search, ChevronRight, Instagram, Facebook, Youtube, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { teamsAPI } from '../../services/api';

const Community = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const data = await teamsAPI.getAll();
        if (data && data.length > 0) {
          // Transform API data to match component's expected format
          const transformedTeams = data.map(team => ({
            id: team.team_id,
            name: team.name,
            logo: team.logo,
            members: team.members?.length || 10,
            location: team.location,
            sponsored: team.sponsored,
            featured: team.featured,
            socials: team.socials || {},
            description: team.description,
          }));
          setTeams(transformedTeams);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);

  const communityStats = [
    { label: 'Registered Players', value: '2,500+', icon: Users },
    { label: 'Active Teams', value: '180+', icon: Award },
    { label: 'Locations', value: '45 States', icon: MapPin },
  ];

  // Get featured team (NY Aces)
  const featuredTeam = teams.find(team => team.featured);
  const otherTeams = teams.filter(team => !team.featured);

  return (
    <section id="community" className="section-padding relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#131416] via-[#0a0b0c] to-[#131416]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#00d4aa] text-sm font-bold uppercase tracking-widest mb-4">
            Player & Team Hub
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4">
            Join the <span className="text-[#00d4aa]">Community</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            Connect with players, form teams, and become part of the GWINS family.
            Your profile, your team, your legacy.
          </p>
          <Button 
            onClick={() => navigate('/community')}
            className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-8 py-3"
          >
            <Users className="mr-2 h-5 w-5" />
            Enter Community Hub
          </Button>
        </div>

        {/* Stats - Hidden until real data is populated
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          {communityStats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-8 rounded-xl bg-white/5 border border-white/5"
            >
              <stat.icon className="h-8 w-8 text-[#00d4aa] mx-auto mb-4" />
              <p className="text-3xl font-black text-white mb-2">{stat.value}</p>
              <p className="text-white/60 text-sm uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>
        */}

        {/* Featured Sponsored Team - NY Aces */}
        {featuredTeam && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-5 w-5 text-[#00d4aa]" />
              <h3 className="text-white text-xl font-bold uppercase tracking-wider">Featured Sponsored Team</h3>
            </div>
            <div className="relative rounded-2xl border border-[#00d4aa]/30 overflow-hidden">
              {/* Team Logo - TOP CENTERED (above image) */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                <div className="w-32 h-32 rounded-xl bg-black/60 backdrop-blur-sm border-2 border-[#00d4aa]/50 flex items-center justify-center overflow-hidden p-3">
                  <img 
                    src={featuredTeam.logo} 
                    alt={featuredTeam.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Background Image - Full Team Photo */}
              <div className="relative w-full">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/157p1moy_NY%20Aces%20%20Team%202.jpg"
                  alt="NY Aces Team"
                  className="w-full h-auto object-contain"
                />
                {/* Gradient overlay at bottom for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>

              {/* Official Sponsor Badge - TOP LEFT */}
              <div className="absolute top-4 left-4 z-20">
                <Badge className="bg-[#00d4aa] text-[#0a0b0c] font-bold text-base px-4 py-2">
                  <Star className="h-4 w-4 mr-2" />
                  Official Sponsor
                </Badge>
              </div>
              
              {/* Content overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex flex-col lg:flex-row gap-6 items-center">
                  {/* Team Details */}
                  <div className="flex-1 text-center lg:text-left">
                    <h4 className="text-3xl font-black text-white mb-2">{featuredTeam.name}</h4>
                    <p className="text-[#00d4aa] font-semibold mb-3">
                      {featuredTeam.location} • {featuredTeam.members} Members
                    </p>
                    <p className="text-white/90 mb-6 max-w-xl">
                      {featuredTeam.description}
                    </p>
                    
                    {/* Social Media Links */}
                    <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                      {featuredTeam.socials?.instagram && (
                        <a
                          href={featuredTeam.socials.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-white/10 hover:bg-[#E4405F]/20 border border-white/20 hover:border-[#E4405F]/50 rounded-full px-4 py-2 transition-all group"
                        >
                          <Instagram className="h-5 w-5 text-white/80 group-hover:text-[#E4405F]" />
                          <span className="text-white/90 text-sm font-medium">@nyacespb</span>
                        </a>
                      )}
                      {featuredTeam.socials?.facebook && (
                        <a
                          href={featuredTeam.socials.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-white/10 hover:bg-[#1877F2]/20 border border-white/20 hover:border-[#1877F2]/50 rounded-full px-4 py-2 transition-all group"
                        >
                          <Facebook className="h-5 w-5 text-white/80 group-hover:text-[#1877F2]" />
                          <span className="text-white/90 text-sm font-medium">Facebook</span>
                        </a>
                      )}
                      {featuredTeam.socials?.youtube && (
                        <a
                          href={featuredTeam.socials.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-white/10 hover:bg-[#FF0000]/20 border border-white/20 hover:border-[#FF0000]/50 rounded-full px-4 py-2 transition-all group"
                        >
                          <Youtube className="h-5 w-5 text-white/80 group-hover:text-[#FF0000]" />
                          <span className="text-white/90 text-sm font-medium">YouTube</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex-shrink-0">
                    <Button className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-6">
                      View Team Profile
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Directory Preview */}
        <div className="bg-[#131416] rounded-2xl border border-white/5 overflow-hidden">
          <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-white text-xl font-bold">Team Directory</h3>
              <p className="text-white/60 text-sm">Find and follow competitive teams</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full"
              />
            </div>
          </div>

          {/* Teams List */}
          <div className="divide-y divide-white/5">
            {otherTeams.map((team) => (
              <div
                key={team.id}
                className="p-6 flex items-center gap-4 hover:bg-white/5 transition-colors cursor-pointer group"
              >
                <img
                  src={team.logo}
                  alt={team.name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-semibold group-hover:text-[#00d4aa] transition-colors">
                      {team.name}
                    </h4>
                    {team.sponsored && (
                      <Badge className="bg-[#00d4aa]/20 text-[#00d4aa] text-xs">Sponsored</Badge>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">
                    {team.members} members • {team.location}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-white/40 group-hover:text-[#00d4aa] transition-colors" />
              </div>
            ))}
          </div>

          {/* View All */}
          <div className="p-6 border-t border-white/5 text-center">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-full"
            >
              View All Teams
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* CTA Cards */}
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {/* Player Registration */}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-[#00d4aa]/10 to-transparent border border-[#00d4aa]/20">
            <Users className="h-10 w-10 text-[#00d4aa] mb-4" />
            <h3 className="text-white text-xl font-bold mb-2">Create Player Profile</h3>
            <p className="text-white/60 mb-6">
              Build your paintball identity. Track stats, join teams, and connect with the community.
            </p>
            <Button 
              onClick={() => navigate('/community')}
              className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Register Now
            </Button>
          </div>

          {/* Team Registration */}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10">
            <Award className="h-10 w-10 text-[#c0c4cc] mb-4" />
            <h3 className="text-white text-xl font-bold mb-2">Register Your Team</h3>
            <p className="text-white/60 mb-6">
              Get your team on the map. Manage rosters, apply for sponsorships, and order custom gear.
            </p>
            <Button
              onClick={() => navigate('/community')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-full"
            >
              <Award className="mr-2 h-4 w-4" />
              Register Team
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Community;
